import { updateLoaderStatus } from './ui.js';
import { openLibraryAdapter } from './api/openlibrary.js';
import { googleBooksAdapter } from './api/googlebooks.js';
import { bnfAdapter } from './api/bnf.js';

/**
 * @typedef {Object} BookResult
 * Objet de résultat normalisé retourné par chaque adaptateur API.
 * Un champ à `null` signifie "non trouvé par cette source".
 * Un champ à `""` (chaîne vide) signifie "trouvé mais vide".
 * Cette distinction est importante pour la fusion : on ne remplace jamais une vraie valeur par null.
 *
 * @property {string|null} title
 * @property {string|null} authors
 * @property {string|null} publisher
 * @property {string|null} series
 * @property {string|null} volume
 * @property {string|null} isbn
 * @property {string|null} coverUrl
 */

/**
 * Interface que chaque adaptateur doit implémenter.
 * @typedef {Object} BookApiAdapter
 * @property {string}   name       - Nom affiché dans le loader ("Open Library", "Google Books"…)
 * @property {Function} fetch      - async (isbn: string) => BookResult | null
 * @property {Function} isComplete - (result: BookResult) => boolean  — peut être surchargée par adaptateur
 */

// Ordre de priorité des sources — modifiable sans toucher à la logique
const ADAPTERS = [
    openLibraryAdapter,
    googleBooksAdapter,
    bnfAdapter,
];

/**
 * Interroge les APIs en cascade et fusionne les résultats.
 * S'arrête dès que le résultat est complet, ou quand toutes les sources ont été interrogées.
 *
 * @param {string} isbn
 * @returns {Promise<BookResult|null>}
 */
export async function fetchBookData(isbn) {
    let result = null;

    for (const adapter of ADAPTERS) {
        // Si le résultat est déjà complet, on s'arrête
        if (result && isComplete(result)) break;

        updateLoaderStatus(`Interrogation de ${adapter.name}…`);

        try {
            const incoming = await adapter.fetch(isbn);

            if (!incoming) continue; // Cette source n'a rien trouvé, on passe

            result = result
                ? mergeResults(result, incoming)  // On fusionne avec ce qu'on a déjà
                : incoming;                        // Premier résultat, on l'utilise tel quel

        } catch (err) {
            console.warn(`${adapter.name} — erreur ignorée :`, err);
            // On continue vers la source suivante même en cas d'erreur réseau
        }
    }

    return result;
}

/**
 * Détermine si un résultat est suffisamment complet pour arrêter le pipeline.
 * Tu peux ajuster les champs "obligatoires" selon tes besoins.
 */
function isComplete(result) {
    return !!(
        result.title &&
        result.authors &&
        result.publisher &&
        result.coverUrl
    );
}

/**
 * Fusionne deux résultats : `base` est complété par `incoming`.
 * Un champ de `base` n'est remplacé que s'il est null ou vide.
 * `incoming` ne peut pas écraser une valeur existante.
 */
function mergeResults(base, incoming) {
    const result = { ...base };

    for (const key of Object.keys(incoming)) {
        const hasValue = result[key] !== null && result[key] !== undefined && result[key] !== '';
        if (!hasValue && incoming[key]) {
            result[key] = incoming[key];
        }
    }

    return result;
}
